<script setup>
/**
 * Grades → levels: the governing surface for the read-across (PD-1293).
 *
 * The ONLY place a link changes or a version is published — the alignment grid is
 * deliberately read-only, so a governance change is never a side effect of browsing.
 * Ported from the design prototype's LvlMappingPanel (lvl/mapping.jsx).
 *
 * Each row is one grade: the evidence behind it, where it currently links, and how
 * much that link can be trusted. An override MOVES the link without touching the
 * evidence, so the confidence pill keeps describing the evidence and the override is
 * marked separately — a moved grade still reading "confident" with nothing to show it
 * had been changed by hand is the specific failure this layout avoids.
 */
import { ref, computed } from 'vue';
import axios from 'axios';
import { isOverridden, compareGrades, publishableEntries, unpublishedChanges } from '@rm-ui/levelling/gradeMapping';

const props = defineProps({
  levellingFramework: { type: Object, required: true },
  schemes: { type: Array, required: true },
  /** linkKey -> suggestion, from the shared engine. */
  suggestions: { type: Object, required: true },
  /** linkKey -> lf_level_id | null, suggestions with overrides applied. */
  map: { type: Object, required: true },
  versions: { type: Array, default: () => [] },
  published: { type: Object, default: null },
  canGovern: { type: Boolean, default: false },
  roles: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'saved']);

const scheme = ref(props.schemes.length ? props.schemes[0].tpa_id : null);
const histOpen = ref(false);
const howOpen = ref(false);
const confirming = ref(false);
const saving = ref(false);
const error = ref('');

const lf = computed(() => props.levellingFramework || {});
const levelLabelWord = computed(() => lf.value.level_label || 'Level');
const levels = computed(() => [...(lf.value.levels || [])].sort((a, b) => a.position - b.position));
const levelById = computed(() => {
  const m = {};
  for (const l of levels.value) m[l.lf_level_id] = l;
  return m;
});

/* The level names as Levels and Tracks shows them, read off the core function's rungs. */
const coreFunctionId = computed(() => {
  const list = lf.value.functions || [];
  const core = list.find(f => f.is_core);
  return (core || list[0] || {}).lf_function_id ?? null;
});
const levelOptionLabel = (l) => {
  const names = [];
  for (const lft of lf.value.level_function_tracks || []) {
    if (lft.lf_function_id !== coreFunctionId.value || lft.lf_level_id !== l.lf_level_id) continue;
    const full = lft.name || '';
    const nm = (full.split(/\s+[-–]\s+/).pop() || full).trim();
    if (nm && !names.includes(nm)) names.push(nm);
  }
  const head = `${levelLabelWord.value} ${l.name}`;
  return names.length ? `${head} · ${names.join(' · ')}` : head;
};
const levelShort = id => (levelById.value[id] ? `${levelLabelWord.value} ${levelById.value[id].name}` : 'Unmapped');

const currentScheme = computed(() => props.schemes.find(s => s.tpa_id === scheme.value) || null);

/** Rows for the chart on screen, read in grade order. */
const rows = computed(() => Object.values(props.suggestions)
  .filter(s => s.tpa_id === scheme.value)
  .sort((a, b) => compareGrades(a.grade, b.grade)));

/* The grades actually asking for a decision. Everything else is calibrated against
   enough evaluated roles and does not need reading. */
const needAttention = computed(() => rows.value.filter(s =>
  props.map[s.key] != null && (s.state === 'thin' || s.state === 'divergent')).length);

const withoutGrade = computed(() => props.roles.filter(r => !String(r.legacyGrade || '').trim()).length);

const changes = computed(() => unpublishedChanges(props.map, props.published));
/* Roles that would arrive at classification pre-filled: a mapped grade, on a chart
   that is not paused. */
const covered = computed(() => {
  const paused = new Set(props.schemes.filter(s => s.paused).map(s => s.tpa_id));
  return props.roles.filter((r) => {
    const g = String(r.legacyGrade || '').trim().toUpperCase();
    if (!g || paused.has(r.gradeTpaId)) return false;
    return props.map[`${r.gradeTpaId}:${g}`] != null;
  }).length;
});

const fmtDate = (t) => {
  try {
    return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) { return ''; }
};

const stateOf = (s) => {
  const to = props.map[s.key];
  if (to == null) return { cls: 'is-off', text: 'Unmapped', tip: 'This grade never pre-fills classification.' };
  if (isOverridden(s, props.map)) {
    return {
      cls: 'is-ov',
      text: 'Overridden',
      tip: `Set by hand. The evidence points at ${levelShort(s.lf_level_id)}. The confidence describes the evidence, which the override did not change.`,
    };
  }
  if (s.state === 'thin') {
    return {
      cls: 'is-thin',
      text: 'Thin evidence',
      tip: `Only ${s.total} evaluated role${s.total === 1 ? '' : 's'} behind this link, below the floor of ${s.floor} — the same floor as anchor coverage.`,
    };
  }
  if (s.state === 'divergent') {
    const where = s.divergentGroups.map(d => `${d.familyGroup} sits at ${levelShort(d.lf_level_id)}`).join('; ');
    return { cls: 'is-div', text: 'Check — divergent', tip: `Same grade, different ${levelLabelWord.value.toLowerCase()}: ${where || 'functions disagree'}. Review before publishing.` };
  }
  return { cls: 'is-ok', text: `Confident · ${Math.round(s.agreement * 100)}%`, tip: 'Calibrated against enough evaluated roles.' };
};

/** Top few level codes the evidence actually landed on, for the Evidence column. */
const evidenceCodes = (s) => Object.keys(s.levels)
  .sort((a, b) => s.levels[b] - s.levels[a])
  .slice(0, 3)
  .map(id => `${levelShort(Number(id))} ×${s.levels[id]}`)
  .join(' · ');

async function post(url, body) {
  saving.value = true;
  error.value = '';
  try {
    const resp = await axios.post(url, body);
    const data = resp && resp.data;
    if (data && data.Status === 'Error') {
      error.value = data.Message || 'That change could not be saved.';
      return false;
    }
    return true;
  } catch (e) {
    error.value = 'That change could not be saved.';
    return false;
  } finally {
    saving.value = false;
  }
}

/* '' is deliberately-unmapped, not "clear the override": the two are different
   decisions and the server stores them differently. */
async function onSelect(s, value) {
  const body = value === '__suggested'
    ? { tpa_id: s.tpa_id, grade: s.grade, reset: true }
    : { tpa_id: s.tpa_id, grade: s.grade, lf_level_id: value === '' ? null : Number(value) };
  if (await post('document/saveGradeMapOverride', body)) emit('saved');
}

async function togglePause() {
  const s = currentScheme.value;
  if (!s) return;
  if (await post('document/setGradeSchemePaused', { tpa_id: s.tpa_id, paused: !s.paused })) emit('saved');
}

async function publish() {
  const entries = publishableEntries(props.map, props.suggestions);
  if (await post('document/publishGradeMap', { entries })) {
    confirming.value = false;
    emit('saved');
  }
}

const nextVersion = computed(() => (props.versions.length
  ? Math.max(...props.versions.map(v => v.n)) + 1 : 1));
const mappedCount = computed(() => Object.values(props.map).filter(v => v != null).length);

/** What changed between two published versions, in the admin's own terms. */
const versionDiff = (v, prev) => {
  if (!prev) return `${v.entries.length} grade${v.entries.length === 1 ? '' : 's'} mapped`;
  const key = e => `${e.tpa_id}:${String(e.grade).trim().toUpperCase()}`;
  const a = {}; const b = {};
  for (const e of v.entries) a[key(e)] = e.lf_level_id;
  for (const e of prev.entries) b[key(e)] = e.lf_level_id;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const moved = [...keys].filter(k => (a[k] ?? null) !== (b[k] ?? null));
  if (!moved.length) return 'no changes';
  const eg = moved.slice(0, 2)
    .map(k => `${k.split(':')[1]} → ${a[k] == null ? 'unmapped' : levelShort(a[k])}`)
    .join(', ');
  return `${moved.length} change${moved.length === 1 ? '' : 's'} · e.g. ${eg}`;
};
const orderedVersions = computed(() => [...props.versions].sort((a, b) => b.n - a.n));
</script>

<template>
  <div class="lvl-map-scrim" @click.self="emit('close')">
    <div class="lvl-map-modal" role="dialog" aria-modal="true" aria-label="Grades to levels">
      <div class="lvl-map-head">
        <div>
          <div class="lvl-map-eyebrow">
            <v-icon size="14">mdi-swap-horizontal</v-icon> Framework mapping
            <span v-if="!canGovern" class="lvl-map-ro">Read-only</span>
          </div>
          <h3>Grades &rarr; {{ levelLabelWord.toLowerCase() }}s</h3>
          <p>
            {{ canGovern ? 'Suggested from evaluated outcomes; publish to pre-fill classification. ' : 'Managed by your comp admin. ' }}
            <template v-if="withoutGrade">{{ withoutGrade }} role{{ withoutGrade === 1 ? '' : 's' }} without a grade
              {{ withoutGrade === 1 ? 'is' : 'are' }} untouched &mdash; they classify normally.</template>
          </p>
        </div>
        <button type="button" class="lvl-map-x" aria-label="Close" @click="emit('close')">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="lvl-map-body">
        <div class="lvl-map-lede">
          <span class="lvl-map-lede-ic"><v-icon size="16">mdi-swap-horizontal</v-icon></span>
          <div class="lvl-map-lede-main">
            <span class="t">&ldquo;Our Grade 11 is their {{ levelLabelWord }} 6&rdquo;</span>
            <span class="s">
              Each of your grades is placed at the {{ levelLabelWord.toLowerCase() }} its evaluated
              roles actually score at, so you can read across in either language.
              <button type="button" class="lvl-map-more" :aria-expanded="howOpen" @click="howOpen = !howOpen">
                {{ howOpen ? 'Hide detail' : 'How this works' }}
              </button>
            </span>
            <span v-if="howOpen" class="s lvl-map-how">
              <span><b>What publishing does.</b> Roles on a mapped grade arrive at classification with
                the {{ levelLabelWord.toLowerCase() }} zone pre-filled, the mapping named as the reason,
                one click to override.</span>
              <span><b>What it never does.</b> It does not change a grade or a
                {{ levelLabelWord.toLowerCase() }}. Already-levelled roles are untouched; grades with no
                link, and paused charts, never pre-fill.</span>
              <span><b>It is meant to be temporary.</b> Every publish is a dated version &mdash; a bridge
                while the business moves onto RoleMapper {{ levelLabelWord.toLowerCase() }}s.</span>
            </span>
          </div>
        </div>

        <div v-if="error" class="lvl-map-error">{{ error }}</div>

        <div class="lvl-map-bar">
          <span v-if="schemes.length > 1" class="lvl-map-schemes" role="tablist" aria-label="Grade chart">
            <v-tooltip v-for="s in schemes" :key="s.tpa_id" bottom content-class="fw-tip">
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  role="tab"
                  :aria-selected="scheme === s.tpa_id"
                  class="lvl-chipbtn"
                  :class="{ 'is-on': scheme === s.tpa_id }"
                  v-bind="attrs"
                  v-on="on"
                  @click="scheme = s.tpa_id"
                >{{ s.label }}{{ s.paused ? ' · paused' : '' }}</button>
              </template>
              <span>Grades captured by &ldquo;{{ s.title }}&rdquo; on {{ s.tmplName
                }}{{ s.paused ? ' — paused, so these grades do not pre-fill classification.' : '.' }}</span>
            </v-tooltip>
          </span>
          <v-tooltip v-if="canGovern && currentScheme" bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="lvl-chipbtn lvl-map-park"
                :disabled="saving"
                v-bind="attrs"
                v-on="on"
                @click="togglePause"
              >
                <v-icon size="14">{{ currentScheme.paused ? 'mdi-play' : 'mdi-pause' }}</v-icon>
                {{ currentScheme.paused ? 'Resume pre-fill' : 'Pause chart' }}
              </button>
            </template>
            <span>{{ currentScheme.paused
              ? 'Resume pre-fill for this chart.'
              : 'Pause this whole chart: its grades stop pre-filling classification (a later migration wave, e.g. an acquired unit). Evidence keeps accumulating; nothing is deleted.' }}</span>
          </v-tooltip>
        </div>

        <div v-if="currentScheme && currentScheme.paused" class="lvl-map-note">
          <v-icon size="16">mdi-pause-circle-outline</v-icon>
          <span><strong>{{ currentScheme.label }}</strong> is paused &mdash; a later migration wave. Its
            grades never pre-fill classification while paused; the mapping and its evidence stay intact.</span>
        </div>

        <div v-if="canGovern && rows.length" class="lvl-map-start">
          <template v-if="needAttention">
            Start with the <b>{{ needAttention }} grade{{ needAttention === 1 ? '' : 's' }}</b> marked
            <b>Thin evidence</b> or <b>Check</b> &mdash; those are the ones asking for a decision. The rest
            are calibrated against enough evaluated roles.
          </template>
          <template v-else>
            Every grade in this chart is calibrated against enough evaluated roles. Review the
            {{ levelLabelWord.toLowerCase() }}s and publish when you are happy.
          </template>
        </div>

        <div class="lvl-map-grid" role="table" aria-label="Grade to level mapping">
          <div class="lvl-map-row lvl-map-row--head" role="row">
            <span>Grade</span><span>Evidence</span><span>Maps to</span><span>Status</span>
          </div>
          <div v-for="s in rows" :key="s.key" class="lvl-map-row" role="row">
            <span class="lvl-grade-pill">{{ s.grade }}</span>
            <span class="lvl-map-ev"><b>{{ s.total }}</b> evaluated &middot; {{ evidenceCodes(s) }}</span>
            <span v-if="canGovern" class="lvl-map-sel">
              <select
                :value="map[s.key] == null ? '' : map[s.key]"
                :disabled="saving"
                :aria-label="`Level for grade ${s.grade}`"
                @change="onSelect(s, $event.target.value)"
              >
                <option value="">Unmapped &mdash; no pre-fill</option>
                <option v-if="isOverridden(s, map)" value="__suggested">
                  Back to suggested &mdash; {{ levelShort(s.lf_level_id) }}
                </option>
                <option v-for="l in levels" :key="l.lf_level_id" :value="l.lf_level_id">
                  {{ levelOptionLabel(l) }}
                </option>
              </select>
            </span>
            <span v-else class="lvl-map-ev">{{ map[s.key] == null ? 'Unmapped' : levelShort(map[s.key]) }}</span>
            <v-tooltip bottom content-class="fw-tip">
              <template v-slot:activator="{ on, attrs }">
                <span class="lvl-map-state" :class="stateOf(s).cls" v-bind="attrs" v-on="on">{{ stateOf(s).text }}</span>
              </template>
              <span>{{ stateOf(s).tip }}</span>
            </v-tooltip>
          </div>
          <div v-if="!rows.length" class="lvl-map-empty">
            No grade on this chart has an evaluated role behind it yet.
          </div>
        </div>

        <div v-if="versions.length" class="lvl-map-hist">
          <button type="button" class="lvl-map-hist-t" :aria-expanded="histOpen" @click="histOpen = !histOpen">
            <v-icon size="15">{{ histOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            Version history &middot; {{ versions.length }}
          </button>
          <div v-if="histOpen">
            <div v-for="(v, i) in orderedVersions" :key="v.n" class="lvl-map-hist-r">
              <b>v{{ v.n }}</b>
              <span>{{ fmtDate(v.at) }}{{ v.by ? ` · ${v.by}` : '' }}</span>
              <span class="d">{{ versionDiff(v, orderedVersions[i + 1]) }}</span>
              <v-tooltip v-if="!v.currentLadder" bottom content-class="fw-tip">
                <template v-slot:activator="{ on, attrs }">
                  <span class="lvl-map-state is-thin" v-bind="attrs" v-on="on">stale</span>
                </template>
                <span>Published against a different ladder, so it is no longer in force &mdash;
                  classification is not pre-filled from it.</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>

      <div class="lvl-map-foot">
        <span class="lvl-map-ver">
          {{ published
            ? `Published v${published.n} · ${fmtDate(published.at)}${published.by ? ' · ' + published.by : ''}`
              + (changes ? ` · ${changes} unpublished change${changes === 1 ? '' : 's'}` : ' · up to date')
            : 'Never published — classification is not pre-filled yet' }}. Transitional by design &mdash;
          saved as dated versions.
        </span>
        <button type="button" class="lvl-map-btn is-text" @click="emit('close')">Close</button>
        <v-tooltip v-if="canGovern" top content-class="fw-tip">
          <template v-slot:activator="{ on, attrs }">
            <!-- The span carries the listeners: a disabled button fires no pointer
                 events, and "why is this greyed out" is exactly when the explanation
                 is wanted. -->
            <span class="lvl-map-btnwrap" v-bind="attrs" v-on="on">
              <button
                type="button"
                class="lvl-map-btn"
                :disabled="saving || (!!published && !changes) || !mappedCount"
                @click="confirming = true"
              ><v-icon size="16">mdi-swap-horizontal</v-icon> Publish mapping</button>
            </span>
          </template>
          <span>{{ !mappedCount ? 'Nothing is mapped yet, so there is nothing to publish.'
            : (!!published && !changes) ? `Nothing has changed since v${published.n}.`
            : 'Save this read-across as a new dated version. From then on, roles on a mapped grade arrive at classification pre-filled.' }}</span>
        </v-tooltip>
      </div>

      <!-- publish confirm -->
      <div v-if="confirming" class="lvl-map-scrim is-inner" @click.self="confirming = false">
        <div class="lvl-map-modal is-confirm" role="dialog" aria-modal="true" aria-label="Publish mapping">
          <div class="lvl-map-head">
            <div><h3>Publish mapping v{{ nextVersion }}?</h3></div>
            <button type="button" class="lvl-map-x" aria-label="Close" @click="confirming = false">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
          <div class="lvl-map-body">
            <div class="lvl-map-sec">
              <span class="lvl-map-sec-ic"><v-icon size="17">mdi-swap-horizontal</v-icon></span>
              <div>
                <span class="h">What changes</span>
                <span class="b">{{ mappedCount }} grades mapped<template v-if="published">
                  &middot; {{ changes }} change{{ changes === 1 ? '' : 's' }} since v{{ published.n }}</template>.
                  From now on, <b>{{ covered }} role{{ covered === 1 ? '' : 's' }}</b> with a covered grade
                  arrive at classification with the {{ levelLabelWord.toLowerCase() }} zone pre-filled
                  &mdash; with the mapping named as the reason, one click to override.</span>
              </div>
            </div>
            <div class="lvl-map-sec">
              <span class="lvl-map-sec-ic"><v-icon size="17">mdi-lock-outline</v-icon></span>
              <div>
                <span class="h">What never changes</span>
                <span class="b">Already-levelled roles are untouched &mdash; a new version only affects
                  future classifications. Unmapped grades and paused charts never pre-fill.</span>
              </div>
            </div>
            <div class="lvl-map-sec">
              <span class="lvl-map-sec-ic"><v-icon size="17">mdi-history</v-icon></span>
              <div>
                <span class="h">Recorded</span>
                <span class="b">Saved as dated version v{{ nextVersion }} &mdash; who, when, and every grade.
                  The link is transitional by design.</span>
              </div>
            </div>
          </div>
          <div class="lvl-map-foot">
            <button type="button" class="lvl-map-btn is-text" @click="confirming = false">Cancel</button>
            <button type="button" class="lvl-map-btn" :disabled="saving" @click="publish">
              <v-icon size="16">mdi-swap-horizontal</v-icon> Publish v{{ nextVersion }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Ported from the prototype's .lvl-map-* block, with --v3-* mapped onto --cc-*. */

.lvl-map-scrim {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(16, 24, 40, .44);
  display: grid;
  place-items: center;
  padding: 24px;
  font-family: var(--cc-font, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);

  &.is-inner { z-index: 310; }
}

.lvl-map-modal {
  width: min(860px, 100%);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 14px;
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, .14));
  overflow: hidden;

  &.is-confirm { width: min(520px, 100%); }
}

.lvl-map-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--cc-border-soft, #eef0f3);

  > div { flex: 1 1 auto; min-width: 0; }
  h3 { margin: 2px 0 0; font-size: 17px; font-weight: 700; color: var(--cc-text, #1f242d); }
  p { margin: 4px 0 0; font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.5; }
}
.lvl-map-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .015em;
  color: var(--cc-text-3, #97a0af);

  ::v-deep .v-icon { color: inherit; }
}
.lvl-map-ro {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  border: 1px solid var(--cc-border, #e5e7eb);
  font-size: 10.5px;
  color: var(--cc-text-3, #97a0af);
}
.lvl-map-x {
  flex: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: var(--cc-text-3, #97a0af);
  padding: 0;

  ::v-deep .v-icon { color: inherit; }
}

.lvl-map-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* Children of a column flex container default to flex-shrink: 1, so against the
     modal's max-height they COMPRESS to fit rather than overflowing — the grade table
     squashes and overflow-y above never has anything to scroll. Pinning them to their
     natural height is what makes the body scrollable. Same trap as the two framework
     views. */
  > * {
    flex-shrink: 0;
  }
}

.lvl-map-lede {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  background: var(--cc-canvas, #fafbfc);
}
.lvl-map-lede-ic {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--cc-accent, #4f46e5) 10%, transparent);
  color: var(--cc-accent, #4f46e5);

  ::v-deep .v-icon { color: inherit; }
}
.lvl-map-lede-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;

  .t { font-size: 13.5px; font-weight: 700; color: var(--cc-text, #1f242d); }
  .s { font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.55; }
}
.lvl-map-how {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--cc-border-soft, #eef0f3);
}
.lvl-map-more {
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-weight: 650;
  color: var(--cc-accent, #4f46e5);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.lvl-map-error {
  padding: 9px 12px;
  border-radius: 9px;
  border: 1px solid var(--cc-warn-bd, #eec391);
  background: var(--cc-warn-bg, #fcf5ee);
  color: var(--cc-warn-fg, #d97706);
  font-size: 12.5px;
}

/* Vuetify renders a tooltip's activator as a SIBLING of an empty
   <span class="v-tooltip">. .lvl-map-row is a four-column grid, so that empty span
   would take a column of its own and shift Status out of line; the flex rows have the
   same problem with their gaps. The wrapper is always empty, so taking it out of
   layout everywhere is both simpler and safer than listing each container. */
.v-tooltip { display: contents; }

.lvl-map-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.lvl-map-schemes { display: inline-flex; gap: 6px; flex-wrap: wrap; }
.lvl-map-park { margin-left: auto; }

.lvl-chipbtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-surface, #ffffff);
  border-radius: 999px;
  padding: 4px 11px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-text-2, #5b6472);
  cursor: pointer;

  &.is-on {
    color: var(--cc-accent, #4f46e5);
    border-color: color-mix(in srgb, var(--cc-accent, #4f46e5) 45%, transparent);
    background: color-mix(in srgb, var(--cc-accent, #4f46e5) 8%, transparent);
  }
  &:disabled { opacity: .5; cursor: default; }
  ::v-deep .v-icon { color: inherit; }
}

.lvl-map-note {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 10px 12px;
  border-radius: 9px;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-canvas, #fafbfc);
  font-size: 12.5px;
  color: var(--cc-text-2, #5b6472);
  line-height: 1.5;

  ::v-deep .v-icon { color: var(--cc-text-3, #97a0af); }
}
.lvl-map-start { font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.55; }

.lvl-map-grid {
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 11px;
  overflow: hidden;
}
.lvl-map-row {
  display: grid;
  grid-template-columns: 90px minmax(0, 1.2fr) minmax(0, 1.4fr) 150px;
  gap: 10px;
  align-items: center;
  padding: 9px 13px;
  border-bottom: 1px solid var(--cc-border-soft, #eef0f3);
  font-size: 12.5px;

  &:last-child { border-bottom: 0; }
  &--head {
    background: var(--cc-canvas, #fafbfc);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    color: var(--cc-text-3, #97a0af);
  }
}
.lvl-map-ev { color: var(--cc-text-2, #5b6472); min-width: 0; overflow-wrap: anywhere; }
.lvl-map-sel select {
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 8px;
  background: var(--cc-surface, #ffffff);
  color: var(--cc-text, #1f242d);
  font-family: inherit;
  font-size: 12px;
}
.lvl-map-empty { padding: 16px 13px; font-size: 12.5px; color: var(--cc-text-3, #97a0af); }

.lvl-grade-pill {
  display: inline-flex;
  align-items: center;
  justify-self: start;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-canvas, #fafbfc);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: .02em;
  color: var(--cc-text-2, #5b6472);
  white-space: nowrap;
}

.lvl-map-state {
  display: inline-flex;
  align-items: center;
  justify-self: start;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--cc-border, #e5e7eb);
  color: var(--cc-text-3, #97a0af);
  cursor: help;
  white-space: nowrap;

  &.is-ok {
    color: var(--cc-ok-fg, #0e9f5b);
    border-color: color-mix(in srgb, var(--cc-ok-fg, #0e9f5b) 45%, transparent);
    background: color-mix(in srgb, var(--cc-ok-fg, #0e9f5b) 8%, transparent);
  }
  &.is-thin { border-style: dashed; }
  &.is-div {
    color: var(--cc-warn-fg, #d97706);
    border-color: var(--cc-warn-bd, #eec391);
    background: var(--cc-warn-bg, #fcf5ee);
  }
  &.is-ov {
    color: var(--cc-violet-fg, #6d5ae6);
    border-color: color-mix(in srgb, var(--cc-violet-fg, #6d5ae6) 45%, transparent);
    background: var(--cc-violet-bg, #efecfc);
  }
  &.is-off { opacity: .8; }
}

.lvl-map-hist { border-top: 1px solid var(--cc-border-soft, #eef0f3); padding-top: 10px; }
.lvl-map-hist-t {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 12.5px;
  font-weight: 650;
  color: var(--cc-text-2, #5b6472);
  cursor: pointer;

  ::v-deep .v-icon { color: inherit; }
}
.lvl-map-hist-r {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 7px 2px;
  font-size: 12px;
  color: var(--cc-text-2, #5b6472);
  border-bottom: 1px solid var(--cc-border-soft, #eef0f3);

  b { color: var(--cc-text, #1f242d); }
  .d { margin-left: auto; color: var(--cc-text-3, #97a0af); text-align: right; }
}

.lvl-map-foot {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-top: 1px solid var(--cc-border-soft, #eef0f3);
  background: var(--cc-canvas, #fafbfc);
}
.lvl-map-ver { flex: 1 1 auto; min-width: 0; font-size: 11.5px; color: var(--cc-text-3, #97a0af); line-height: 1.5; }

/* The publish button needs a real box around it to hover on while disabled, and that
   box becomes the flex item in its place — so it inherits the sizing the button had.
   It cannot be display: contents: an element with no box receives no pointer events,
   and the tooltip would never open, which is the one case it exists for. */
.lvl-map-btnwrap { flex: none; display: inline-flex; }
.lvl-map-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: none;
  border: 1px solid var(--cc-accent, #4f46e5);
  background: var(--cc-accent, #4f46e5);
  color: #fff;
  border-radius: 9px;
  padding: 7px 14px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 650;
  cursor: pointer;

  &.is-text { background: transparent; border-color: transparent; color: var(--cc-text-2, #5b6472); }
  &:disabled { opacity: .45; cursor: default; }
  ::v-deep .v-icon { color: inherit; }
}

.lvl-map-sec {
  display: flex;
  align-items: flex-start;
  gap: 11px;

  > div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .h { font-size: 12.5px; font-weight: 700; color: var(--cc-text, #1f242d); }
  .b { font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.55; }
}
.lvl-map-sec-ic {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: var(--cc-canvas, #fafbfc);
  color: var(--cc-text-3, #97a0af);

  ::v-deep .v-icon { color: inherit; }
}
</style>
